import type { StepView, WorkflowPeopleView } from '../types'

/** What a step does — the engine's own names. */
export type StepKind = 'APPROVAL' | 'NOTIFICATION' | 'CONDITION' | 'DELAY'

/**
 * What the palette offers. "Parallel approval" is an approval whose mode asks everybody at
 * once — one kind underneath, two cards on screen, because that is how people think of it.
 */
export type PaletteItem = 'APPROVAL' | 'PARALLEL' | 'NOTIFICATION' | 'CONDITION' | 'DELAY'

/** Who a step names, in the builder's three-way picker: a role, a person, or a relation to the record. */
export type RecipientKind = 'ROLE' | 'USER' | 'RELATION'

export interface RecipientDraft {
  key: string
  kind: RecipientKind
  /** A role id, a user id, or one of {@link RELATIONS}' values. */
  value: string
}

export interface ConditionDraft {
  key: string
  field: string
  operator: string
  value: string
}

export interface StepDraft {
  /** The screen's own id for the card; never sent. */
  key: string
  kind: StepKind
  name: string
  description: string
  mode: 'SEQUENTIAL' | 'PARALLEL_ALL' | 'PARALLEL_ANY'
  recipients: RecipientDraft[]
  conditions: ConditionDraft[]
  slaHours: string
  escalationType: string
  allowSkip: boolean
  allowSelfApproval: boolean
  notifyEmail: boolean
  delayAmount: string
  delayUnit: 'HOURS' | 'DAYS'
}

/**
 * The relations a step can name, mapped onto the engine's resolvers. "Reporting manager" is
 * the first step up the management line; "Manager's manager" the second.
 */
export const RELATIONS: { value: string; label: string }[] = [
  { value: 'MANAGER_1', label: 'Reporting manager' },
  { value: 'MANAGER_2', label: "Manager's manager" },
  { value: 'DEPARTMENT_HEAD', label: 'Department head' },
  { value: 'COST_CENTER_OWNER', label: 'Cost centre owner' },
  { value: 'INITIATOR', label: 'The employee' },
]

export const OPERATORS: { value: string; label: string; needsValue: boolean }[] = [
  { value: 'EQ', label: 'is', needsValue: true },
  { value: 'NE', label: 'is not', needsValue: true },
  { value: 'GT', label: 'is more than', needsValue: true },
  { value: 'GTE', label: 'is at least', needsValue: true },
  { value: 'LT', label: 'is less than', needsValue: true },
  { value: 'LTE', label: 'is at most', needsValue: true },
  { value: 'IN', label: 'is one of', needsValue: true },
  { value: 'NOT_IN', label: 'is none of', needsValue: true },
  { value: 'CONTAINS', label: 'contains', needsValue: true },
  { value: 'IS_NULL', label: 'is empty', needsValue: false },
  { value: 'IS_NOT_NULL', label: 'is not empty', needsValue: false },
]

export const ESCALATIONS: { value: string; label: string }[] = [
  { value: 'NOTIFY_ONLY', label: 'Remind the approver' },
  { value: 'ESCALATE_TO_MANAGER', label: "Escalate to the approver's manager" },
  { value: 'AUTO_APPROVE', label: 'Approve automatically' },
]

let counter = 0
/** A key unique within this page's life — enough for React and for errors to find their card. */
export function nextKey(prefix = 'k'): string {
  counter += 1
  return `${prefix}${counter}`
}

function recipient(kind: RecipientKind, value: string): RecipientDraft {
  return { key: nextKey('r'), kind, value }
}

/** A fresh step from the palette, with defaults that would already work. */
export function newStep(item: PaletteItem): StepDraft {
  const base: StepDraft = {
    key: nextKey('s'), kind: 'APPROVAL', name: '', description: '', mode: 'SEQUENTIAL',
    recipients: [], conditions: [], slaHours: '', escalationType: 'NOTIFY_ONLY',
    allowSkip: false, allowSelfApproval: false, notifyEmail: false, delayAmount: '1', delayUnit: 'DAYS',
  }
  switch (item) {
    case 'APPROVAL':
      return { ...base, name: 'Manager approval', recipients: [recipient('RELATION', 'MANAGER_1')] }
    case 'PARALLEL':
      return {
        ...base, name: 'Parallel approval', mode: 'PARALLEL_ALL',
        recipients: [recipient('RELATION', 'MANAGER_1'), recipient('RELATION', 'DEPARTMENT_HEAD')],
      }
    case 'NOTIFICATION':
      return { ...base, kind: 'NOTIFICATION', name: 'Notify', recipients: [recipient('RELATION', 'INITIATOR')] }
    case 'CONDITION':
      return {
        ...base, kind: 'CONDITION', name: 'Condition',
        conditions: [{ key: nextKey('c'), field: '', operator: 'GT', value: '' }],
      }
    case 'DELAY':
      return { ...base, kind: 'DELAY', name: 'Wait' }
  }
}

function recipientFromRule(rule: StepView['approvers'][number]): RecipientDraft | null {
  switch (rule.resolver) {
    case 'ROLE': return recipient('ROLE', rule.roleId ?? '')
    case 'SPECIFIC_USER': return recipient('USER', rule.userId ?? '')
    case 'MANAGER_CHAIN': return recipient('RELATION', `MANAGER_${rule.managerLevel ?? 1}`)
    case 'DEPARTMENT_HEAD':
    case 'COST_CENTER_OWNER':
    case 'INITIATOR':
      return recipient('RELATION', rule.resolver)
    default:
      // A position rule from the older screen. Not offered here; kept out rather than
      // shown as something it is not — the save refuses nothing it would lose silently,
      // because the step then names fewer people and the dry run says so.
      return null
  }
}

/** A saved step, as the builder edits it. */
export function fromView(step: StepView): StepDraft {
  const kind = (step.kind || 'APPROVAL') as StepKind
  const minutes = step.delayMinutes ?? 1440
  const inDays = minutes % 1440 === 0
  return {
    key: nextKey('s'),
    kind,
    name: step.name,
    description: step.description ?? '',
    mode: (step.mode || 'SEQUENTIAL') as StepDraft['mode'],
    recipients: step.approvers.map(recipientFromRule).filter((row): row is RecipientDraft => row !== null),
    conditions: step.conditions.map((condition) => ({
      key: nextKey('c'), field: condition.field, operator: condition.operator, value: condition.value ?? '',
    })),
    slaHours: step.slaHours == null ? '' : String(step.slaHours),
    escalationType: step.escalationType || 'NOTIFY_ONLY',
    allowSkip: step.allowSkip,
    allowSelfApproval: step.allowSelfApproval,
    notifyEmail: step.notifyEmail,
    delayAmount: String(inDays ? minutes / 1440 : Math.max(1, Math.round(minutes / 60))),
    delayUnit: inDays ? 'DAYS' : 'HOURS',
  }
}

function ruleFor(row: RecipientDraft) {
  const empty = { roleId: null, userId: null, positionId: null, managerLevel: null }
  if (row.kind === 'ROLE') return { ...empty, resolver: 'ROLE', roleId: row.value || null }
  if (row.kind === 'USER') return { ...empty, resolver: 'SPECIFIC_USER', userId: row.value || null }
  if (row.value.startsWith('MANAGER_')) {
    return { ...empty, resolver: 'MANAGER_CHAIN', managerLevel: Number(row.value.slice(8)) || 1 }
  }
  return { ...empty, resolver: row.value || 'INITIATOR' }
}

export function delayMinutes(step: StepDraft): number | null {
  const amount = Number(step.delayAmount)
  if (!Number.isFinite(amount) || amount <= 0) return null
  return Math.round(amount * (step.delayUnit === 'DAYS' ? 1440 : 60))
}

/** The steps as the API takes them — only what each kind uses. */
export function toPayload(steps: StepDraft[]) {
  return steps.map((step, index) => {
    const namesPeople = step.kind === 'APPROVAL' || step.kind === 'NOTIFICATION'
    const sla = Number(step.slaHours)
    return {
      stepOrder: index + 1,
      name: step.name.trim(),
      kind: step.kind,
      description: step.description.trim() || null,
      mode: step.kind === 'APPROVAL' ? step.mode : 'SEQUENTIAL',
      allowSkip: step.kind === 'APPROVAL' && step.allowSkip,
      allowSelfApproval: step.kind === 'APPROVAL' && step.allowSelfApproval,
      slaHours: step.kind === 'APPROVAL' && step.slaHours.trim() && Number.isFinite(sla) ? sla : null,
      escalationType: step.kind === 'APPROVAL' ? step.escalationType : 'NOTIFY_ONLY',
      approvers: namesPeople ? step.recipients.map(ruleFor) : [],
      conditions: step.kind === 'CONDITION'
        ? step.conditions.filter((row) => row.field.trim()).map((row) => ({
          field: row.field.trim(),
          operator: row.operator,
          value: OPERATORS.find((op) => op.value === row.operator)?.needsValue === false
            ? null : row.value.trim() || null,
        }))
        : [],
      notifyEmail: step.kind === 'NOTIFICATION' && step.notifyEmail,
      delayMinutes: step.kind === 'DELAY' ? delayMinutes(step) : null,
    }
  })
}

export function insertAt<T>(list: T[], index: number, item: T): T[] {
  return [...list.slice(0, index), item, ...list.slice(index)]
}

export function move<T>(list: T[], index: number, by: -1 | 1): T[] {
  const target = index + by
  if (target < 0 || target >= list.length) return list
  const next = [...list]
  const [taken] = next.splice(index, 1)
  next.splice(target, 0, taken as T)
  return next
}

/** One person or rule, in words. */
export function recipientLabel(row: RecipientDraft, people: WorkflowPeopleView | undefined): string {
  if (row.kind === 'ROLE') {
    return people?.roles.find((role) => role.id === row.value)?.label ?? 'A role (choose one)'
  }
  if (row.kind === 'USER') {
    return people?.users.find((user) => user.id === row.value)?.label ?? 'A person (choose one)'
  }
  const relation = RELATIONS.find((option) => option.value === row.value)
  if (relation) return relation.label
  return row.value.startsWith('MANAGER_') ? `Manager, ${row.value.slice(8)} levels up` : row.value
}

/** The line under a card's name: who, what is tested, or how long. */
export function summary(step: StepDraft, people: WorkflowPeopleView | undefined,
  fieldLabel: (name: string) => string): string {
  if (step.kind === 'CONDITION') {
    const rules = step.conditions.filter((row) => row.field.trim())
    if (rules.length === 0) return 'No rule yet'
    return rules.map((row) => {
      const operator = OPERATORS.find((op) => op.value === row.operator)
      return `${fieldLabel(row.field)} ${operator?.label ?? row.operator}${operator?.needsValue === false ? '' : ` ${row.value}`}`
    }).join(' and ')
  }
  if (step.kind === 'DELAY') {
    const amount = Number(step.delayAmount) || 0
    const unit = step.delayUnit === 'DAYS' ? (amount === 1 ? 'day' : 'days') : (amount === 1 ? 'hour' : 'hours')
    return `Waits ${amount} ${unit}`
  }
  if (step.recipients.length === 0) return 'Nobody yet'
  return step.recipients.map((row) => recipientLabel(row, people)).join(', ')
}

/** Which step and which part of it a server field error belongs to. */
export function locateError(field: string): { index: number; part: string } | null {
  const match = /^steps\[(\d+)]\.?([a-zA-Z]*)/.exec(field)
  if (!match) return null
  const part = match[2] === 'approvers' ? 'recipients' : match[2] || 'name'
  return { index: Number(match[1]), part }
}
